import { useState, useCallback, useEffect, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useGetRestaurantRatings } from '@/api/queries/useRestaurantQueries';
import { useSubmitRestaurantRating, useUploadMenuItemImage } from '@/api/mutations/useRestaurantMutations';
import { restaurantKeys } from '@/api/keys';
import { haptics } from '@/utils/haptics';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { CATEGORY_ORDER, MAX_PENDING_IMAGES } from '../ratingCategories';
import type { SwipeDirection } from '../ratingCategories';
import type { RestaurantRatings, MenuItemRating } from '@/types/ratings';

/** Lifecycle of a locally-staged menu item photo, from pick to server link. */
export type PendingImageStatus = 'pending' | 'uploading' | 'linked' | 'failed';

/**
 * True for a status that is safe to send to a discard call — a still-local
 * pick or one that failed to link. Never true for 'uploading' or 'linked':
 * a server-linked image must never be discarded.
 */
export function isDiscardable(status: PendingImageStatus): boolean {
  return status === 'pending' || status === 'failed';
}

/**
 * Fans out pending-photo uploads for rated menu items after a ratings
 * submission has already succeeded. Standalone (not a hook) so it is
 * independently unit-testable — the caller passes a pre-bound `uploadFn`
 * that already closes over `restaurantId`.
 *
 * Never throws for individual image failures; only a status-tracking
 * callback observes per-image outcomes. Callers that need to know when the
 * whole batch has settled can `.then()`/`.catch()` the returned promise, but
 * MUST NOT let a rejection here affect `submit()` itself (see
 * useRestaurantRating.submit — this is deliberately fired without await).
 */
export async function uploadPendingMenuItemImages(
  menuItems: RatingMenuItem[],
  responseMenuItemRatings: MenuItemRating[] | null, // server coerces empty array to null
  uploadFn: (params: { menuItemId: number; publicId: string }) => Promise<unknown>,
  onStatusChange: (itemId: string, publicId: string, status: 'uploading' | 'linked' | 'failed') => void,
): Promise<void> {
  const responseItems = responseMenuItemRatings ?? [];
  const itemsWithPendingImages = menuItems.filter((item) =>
    item.pendingImages.some((img) => img.status === 'pending')
  );
  if (itemsWithPendingImages.length === 0) return;

  const uploads = itemsWithPendingImages.flatMap((item) => {
    // response's menuItemRatings is the RESTAURANT'S ENTIRE MENU, not just items
    // submitted this session. Server get_or_creates by EXACT name match after
    // trimming (views/restaurant.py:1473). updateMenuItem does NOT trim locally,
    // so match against a trimmed local name to be safe.
    const resolvedId = item.menuItemId
      ?? responseItems.find((r) => r.name === item.name.trim())?.menuItemId;
    if (resolvedId === undefined) return [];
    return item.pendingImages
      .filter((img) => img.status === 'pending')
      .map(async (img) => {
        onStatusChange(item.id, img.publicId, 'uploading');
        try {
          await uploadFn({ menuItemId: resolvedId, publicId: img.publicId });
          onStatusChange(item.id, img.publicId, 'linked');
        } catch (e) {
          onStatusChange(item.id, img.publicId, 'failed');
          throw e; // re-thrown so allSettled records it rejected
        }
      });
  });

  const results = await Promise.allSettled(uploads);
  const failures = results.filter((r) => r.status === 'rejected');
  if (failures.length > 0) {
    console.error(`${failures.length} of ${uploads.length} menu item photo uploads failed:`, failures);
  }
}

/**
 * Submission-time shape for a menu item rating.
 * Differs from the response-time `MenuItemRating` in two ways:
 * - `menuItemId` is optional: new (user-added) items have no server PK yet
 * - `course` is included: the API persists the course on creation
 * The cast to `MenuItemRating[]` in `submit()` is intentional — the server
 * accepts this superset and the response type is a separate concern.
 */
interface SubmitMenuItemRating extends Omit<MenuItemRating, 'menuItemId'> {
  menuItemId?: number;
  course: string;
}

/** The six rating categories for a restaurant visit. */
export type RatingCategory = 'atmosphere' | 'service' | 'presentation' | 'food' | 'value' | 'overall';

// Course types available for user selection (excluding salad/soup)
export const USER_SELECTABLE_COURSES = [
  { value: 'appetizer', label: 'Appetizer' },
  { value: 'main_course', label: 'Main Course' },
  { value: 'dessert', label: 'Dessert' },
  { value: 'juice', label: 'Juice' },
  { value: 'soft_drinks', label: 'Soft Drinks' },
  { value: 'hot_drinks', label: 'Hot Drinks' },
  { value: 'zero_proof', label: 'Zero Proof' },
  { value: 'water', label: 'Water' },
  { value: 'wine', label: 'Wine' },
  { value: 'beer', label: 'Beer' },
  { value: 'spirits', label: 'Spirits' },
  { value: 'cocktails', label: 'Cocktails' },
  { value: 'bottled_cocktails', label: 'Bottled Cocktails' },
] as const;

export type CourseType = (typeof USER_SELECTABLE_COURSES)[number]['value'];

export interface RatingMenuItem {
  /** Temporary ID for new items, or server ID for existing */
  id: string;
  /** Server-side menu item ID (undefined for new items) */
  menuItemId?: number;
  /** Menu item name */
  name: string;
  /** Course type (appetizer, main_course, etc.) */
  course: CourseType;
  /** Rating value (0 if not rated) */
  rating: number;
  /** Image public IDs staged for upload, and their lifecycle status. */
  pendingImages: Array<{ publicId: string; previewUrl: string; status: PendingImageStatus }>;
  /** Whether this is a newly added item */
  isNew: boolean;
}

/** View union replacing the 8-element RATING_STEPS step-index model. */
export type ActiveView = 'ratings' | 'menu_items' | 'completion';

/** Zero-value ratings object used for both initial state and reset. */
const EMPTY_RATINGS = {
  atmosphere: 0,
  service: 0,
  presentation: 0,
  food: 0,
  value: 0,
  overall: 0,
} as const;

interface UseRestaurantRatingOptions {
  restaurantId: number;
  /** Whether to enable fetching ratings - use to defer loading until modal is open */
  enabled?: boolean;
  /**
   * Land on the menu-items step instead of the first category — used by the
   * restaurant profile's "Rate a menu item" entry point. Decided ONCE per
   * open, from the ratings as first loaded: honoured ONLY when the member
   * already had all six category ratings on file at that moment (rating the
   * last missing category mid-flow never triggers a jump): `submit()` posts
   * the whole `restaurantRatings` object, so jumping past the booth with
   * unrated categories would submit zeros for them. When any category is
   * unrated the flow opens at the normal first step (the member must rate the
   * categories first, same as the existing Rate button). paire-app#122/#74.
   */
  startAtMenuItems?: boolean;
}

interface UseRestaurantRatingReturn {
  // View navigation
  activeView: ActiveView;
  goToRatings: () => void;
  goToCompletion: () => void;

  // Restaurant ratings
  ratings: RestaurantRatings;

  // Category-step navigation (the one-category-at-a-time booth)
  /** Index into CATEGORY_ORDER for the category currently on screen. */
  categoryStep: number;
  /** The category currently on screen. */
  currentCategory: RatingCategory;
  /** True if `categoryStep` is on the last category (drives the "Rate Menu Items" vs "Next" label). */
  isLastCategory: boolean;
  /** True if the Next action (manual click/swipe) is currently allowed. */
  canGoNextCategory: boolean;
  /** True if the Prev action is currently allowed (not on the first category). */
  canGoPrevCategory: boolean;
  /** Direction of the current/most recent category transition, for the slide animation. */
  swipeDirection: SwipeDirection;
  /** True while the "please rate this category first" nudge should be shown. */
  nudge: boolean;
  /** Bumped every time a blocked-Next nudge fires, to force a fresh screen-reader announcement. */
  nudgeAnnounceKey: number;
  /** Combined step index across the whole booth (0-5 categories, 6 menu items, 7 completion). */
  globalStep: number;
  /** Manual "Next" navigation — click or swipe-left. */
  goNextCategoryStep: () => void;
  /** Manual "Prev" navigation — click or swipe-right. */
  goPrevCategoryStep: () => void;
  /** Jump directly to a category step (dots navigation). Steps 6/7 are not reachable via this fn. */
  goToStep: (step: number) => void;
  /** Set a category's rating and auto-advance 400ms later, first tap only (per session). */
  setRatingAndAdvance: (category: RatingCategory, value: number) => void;

  // Menu items
  menuItems: RatingMenuItem[];
  addMenuItem: (name: string, course: CourseType) => void;
  updateMenuItem: (id: string, updates: Partial<RatingMenuItem>) => void;
  removeMenuItem: (id: string) => void;
  addMenuItemImage: (itemId: string, publicId: string, previewUrl: string) => void;
  removeMenuItemImage: (itemId: string, publicId: string) => void;

  // Submission
  submit: () => Promise<void>;
  isSubmitting: boolean;
  isLoading: boolean;
  reset: () => void;

  // Load error state
  /** True when the ratings fetch has errored and no in-flight retry is in progress. */
  loadError: boolean;
  /** Re-trigger the ratings fetch after an error. Exposed as onRetry for consumer clarity. */
  onRetry: () => void;
}

/**
 * Custom hook for managing restaurant rating flow state.
 *
 * Navigation is modelled as a three-state union (`activeView`) for the
 * top-level view, plus a `categoryStep` index within the `ratings` view for
 * the one-category-at-a-time booth. This hook owns all navigation truth —
 * the shell and progress dots are pure consumers of `globalStep`/`goToStep`.
 */
export function useRestaurantRating({
  restaurantId,
  enabled = true,
  startAtMenuItems = false,
}: UseRestaurantRatingOptions): UseRestaurantRatingReturn {
  // Fetch existing ratings - only when enabled (modal is open)
  const {
    data: existingData,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useGetRestaurantRatings(restaurantId, { enabled });
  const { mutateAsync: submitRating, isPending: isSubmitting } = useSubmitRestaurantRating();
  const { mutateAsync: uploadMenuItemImageMutation } = useUploadMenuItemImage();
  const queryClient = useQueryClient();
  const toast = useToast();
  /** Ensures the "some photos failed" toast fires at most once per submit cycle, not once per failed image. */
  const hasShownUploadFailureToastRef = useRef(false);
  /**
   * True once the guest has closed the modal (reset() has run). Guards
   * onImageStatusChange's failure toast — a photo upload that fails AFTER
   * close must be absorbed silently rather than surfacing on whatever
   * screen the guest has since navigated to. Cleared whenever the modal
   * (re)opens, since the hook instance survives close/reopen.
   */
  const isClosedRef = useRef(false);
  /**
   * True once `submit()` has been called this session. Guards the
   * existing-data init effect below: the ratings-submit mutation's
   * `onSuccess` invalidates `restaurantKeys.ratings`, and this hook instance
   * is still mounted (and its `useGetRestaurantRatings` still subscribed)
   * while the guest is on CompletionStep, so the invalidation triggers a
   * refetch with a new `existingData` reference. Without this guard the
   * effect would re-fire and `setMenuItems` would replace the whole array
   * with server-derived items (`pendingImages: []`), wiping out local
   * upload-status tracking for photos still uploading in the background
   * fan-out. Cleared in reset() AND on reopen below (same
   * survives-close/reopen lifetime as isClosedRef above) — a guest who
   * closes after submitting and later reopens to re-rate must still get
   * fresh `existingData` populated into `menuItems`.
   */
  const hasSubmittedRef = useRef(false);
  /**
   * Must stay declared ABOVE the existing-data init effect further down
   * (the one keyed on `[existingData, enabled]`, which reads
   * `hasSubmittedRef.current`). React flushes effects in declaration order,
   * so this ordering guarantees the reset below always runs before that
   * effect's read on the same render pass — reopen-after-submit correctness
   * depends on it. Do not reorder these two effects.
   */
  useEffect(() => {
    if (enabled) {
      isClosedRef.current = false;
      hasSubmittedRef.current = false;
    }
  }, [enabled]);

  // View navigation state — initial value is 'ratings' (the category rating booth)
  const [activeView, setActiveView] = useState<ActiveView>('ratings');

  // Rating state
  const [ratings, setRatings] = useState<RestaurantRatings>({ ...EMPTY_RATINGS });

  // Menu items state
  const [menuItems, setMenuItems] = useState<RatingMenuItem[]>([]);

  // Category-step booth state
  const [categoryStep, setCategoryStep] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState<SwipeDirection>('right');
  const [nudge, setNudge] = useState(false);
  // Toggled so the aria-live region's text NODE actually changes even when the
  // message is identical — an unchanged string set twice in a row is not
  // guaranteed to be re-announced by every screen reader.
  const [nudgeAnnounceKey, setNudgeAnnounceKey] = useState(0);

  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearAdvanceTimer = () => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  };
  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearNudgeTimer = () => {
    if (nudgeTimerRef.current) {
      clearTimeout(nudgeTimerRef.current);
      nudgeTimerRef.current = null;
    }
  };
  // Hook is instantiated above the shell's early return (`if (!isOpen) return
  // null`), so this state survives close/reopen. Clear both timers on
  // unmount to avoid a leaked timer bumping categoryStep or flashing the
  // nudge on the NEXT open.
  useEffect(() => () => {
    clearAdvanceTimer();
    clearNudgeTimer();
  }, []);

  // Session-local tracking, independent of whether a category arrived
  // pre-filled from the server. Cleared in reset().
  const advancedCategoriesRef = useRef<Set<RatingCategory>>(new Set());

  // Initialize from existing data whenever data changes or modal opens (enabled: false→true)
  useEffect(() => {
    if (!enabled || !existingData || hasSubmittedRef.current) return;

    // Set restaurant ratings
    if (existingData.restaurantRatings) {
      setRatings(existingData.restaurantRatings);
    }

    // Convert existing menu item ratings
    if ((existingData.menuItemRatings?.length ?? 0) > 0) {
      setMenuItems(
        existingData.menuItemRatings.map((item: MenuItemRating) => ({
          id: `existing-${item.menuItemId}`,
          menuItemId: item.menuItemId,
          name: item.name,
          course: 'main_course' as CourseType, // Server does not return course for existing items — display only.
          rating: item.rating,
          pendingImages: [],
          isNew: false,
        }))
      );
    }
  }, [existingData, enabled]);

  // Check if all 6 categories are rated (required before proceeding to menu items).
  const allCategoriesRated = CATEGORY_ORDER.every((category) => (ratings[category] ?? 0) > 0);

  /**
   * One-shot jump to the menu-items step for `startAtMenuItems` opens. The
   * decision is made ONCE per open, the first time the loaded ratings
   * (`existingData`) are available, regardless of outcome: jump only if that
   * INITIAL data already has all six categories rated. Deciding from
   * `existingData` (not live `ratings`) means a member who rates the last
   * missing category mid-flow is never yanked to the menu step. The ref is
   * re-armed when the modal closes (`enabled` false) and in reset(), and the
   * jump never fires after the member has navigated back from the menu step.
   */
  const hasDecidedJumpRef = useRef(false);
  useEffect(() => {
    if (!enabled) {
      hasDecidedJumpRef.current = false;
      return;
    }
    if (!startAtMenuItems || hasDecidedJumpRef.current || hasSubmittedRef.current) return;
    if (!existingData) return;
    hasDecidedJumpRef.current = true;
    const initiallyComplete = CATEGORY_ORDER.every(
      (category) => (existingData.restaurantRatings?.[category] ?? 0) > 0
    );
    if (initiallyComplete) setActiveView('menu_items');
  }, [enabled, startAtMenuItems, existingData]);

  /**
   * `loadError` is true when the fetch has failed and no retry is in flight.
   * We use `isFetching` (not `isLoading`) because `isLoading` is only true on
   * initial mount with no cached data; `isFetching` is true during any
   * in-flight request including retries — which is the correct window to
   * suppress a stale error from the previous attempt.
   */
  const loadError = isError && !isFetching;

  const currentCategory = CATEGORY_ORDER[categoryStep];
  const currentCategoryRated = (ratings[currentCategory] ?? 0) > 0;
  const isLastCategory = categoryStep === CATEGORY_ORDER.length - 1;
  const canGoNextCategory = isLastCategory ? allCategoriesRated : currentCategoryRated;
  const canGoPrevCategory = categoryStep > 0;

  const globalStep =
    activeView === 'ratings'
      ? categoryStep
      : activeView === 'menu_items'
        ? CATEGORY_ORDER.length
        : CATEGORY_ORDER.length + 1;

  // Navigation callbacks
  const goToRatings = useCallback(() => {
    haptics.selection();
    clearAdvanceTimer();
    // Land on the LAST category ("Overall"), not step 0 — matches the flow's
    // natural re-entry point when returning from the dish carousel.
    setCategoryStep(CATEGORY_ORDER.length - 1);
    setActiveView('ratings');
  }, []);

  const goToMenuItems = useCallback(() => {
    if (!allCategoriesRated) return;
    haptics.selection();
    setActiveView('menu_items');
  }, [allCategoriesRated]);

  const goToCompletion = useCallback(() => {
    haptics.selection();
    setActiveView('completion');
  }, []);

  // Dots may only target category indices (0–5) — steps 6/7 are reachable
  // ONLY through the flow. This function is the enforcement point; the dots
  // component must not wire onClick to anything else.
  const goToStep = useCallback((step: number) => {
    if (step < 0 || step >= CATEGORY_ORDER.length) return;
    clearAdvanceTimer();
    setSwipeDirection(step > categoryStep ? 'right' : 'left');
    setCategoryStep(step);
    setActiveView('ratings');
  }, [categoryStep]);

  // Manual navigation (click/gesture) — synchronous, always reads the CURRENT
  // render's canGoNextCategory/allCategoriesRated. No stale-closure risk here.
  const goNextCategoryStep = useCallback(() => {
    if (!canGoNextCategory) {
      haptics.error();
      clearNudgeTimer();
      setNudge(true);
      setNudgeAnnounceKey((k) => k + 1);
      nudgeTimerRef.current = setTimeout(() => {
        setNudge(false);
        nudgeTimerRef.current = null;
      }, 2000);
      return;
    }
    clearAdvanceTimer();
    setSwipeDirection('right');
    if (isLastCategory) {
      goToMenuItems();
    } else {
      setCategoryStep((s) => s + 1);
    }
  }, [canGoNextCategory, isLastCategory, goToMenuItems]);

  const goPrevCategoryStep = useCallback(() => {
    clearAdvanceTimer();
    setSwipeDirection('left');
    setCategoryStep((s) => Math.max(s - 1, 0));
  }, []);

  // Rating functions
  const setRating = useCallback((category: RatingCategory, value: number) => {
    setRatings((prev) => {
      const previousValue = prev[category] ?? 0;
      // Track rating change if there was a previous value (not initial rating)
      if (previousValue > 0 && previousValue !== value) {
        analytics.capture(AnalyticsEvents.RESTAURANT_RATING_CHANGED, {
          restaurant_id: restaurantId,
          rating_type: category,
          from_rating: previousValue,
          to_rating: value,
        });
      }
      return { ...prev, [category]: value };
    });
  }, [restaurantId]);

  // Ref mirror of `ratings`, updated SYNCHRONOUSLY inside setRatingAndAdvance
  // itself (not via a useEffect) — this is the stale-closure fix documented
  // below.
  const ratingsRef = useRef(ratings);
  useEffect(() => {
    ratingsRef.current = ratings;
  }, [ratings]);

  // STALE-CLOSURE BUG FIX: a naive
  // setRatingAndAdvance's useCallback would close over `goToMenuItems`,
  // which closes over `allCategoriesRated` AS OF THE RENDER THAT CREATED
  // THAT FUNCTION INSTANCE. Because the 400ms timer fires asynchronously,
  // and the callback isn't re-created until AFTER the state update from
  // setRating() has propagated through a render, a closed-over
  // goToMenuItems INSIDE an already-scheduled timer would be bound to the
  // OLD (pre-this-rating) allCategoriesRated value. Concretely: rating
  // "Overall" last would silently fail to advance to the dish step. Fix:
  // never call the closed-over goToMenuItems from inside the timer;
  // recompute the gate from ratingsRef (synchronous) and call setActiveView
  // directly.
  const setRatingAndAdvance = useCallback((category: RatingCategory, value: number) => {
    haptics.selection(); // immediate tap feedback — fires on EVERY tap, first or repeat
    const isFirstTapThisSession = !advancedCategoriesRef.current.has(category);
    setRating(category, value);
    ratingsRef.current = { ...ratingsRef.current, [category]: value };
    if (!isFirstTapThisSession) return;
    advancedCategoriesRef.current.add(category);
    clearAdvanceTimer();
    advanceTimerRef.current = setTimeout(() => {
      haptics.selection(); // transition haptic — separate firing, not a dedupe
      setSwipeDirection('right');
      const isLast = CATEGORY_ORDER.indexOf(category) === CATEGORY_ORDER.length - 1;
      if (isLast) {
        const freshAllRated = CATEGORY_ORDER.every((c) => (ratingsRef.current[c] ?? 0) > 0);
        if (freshAllRated) setActiveView('menu_items'); // bypasses goToMenuItems() deliberately
      } else {
        setCategoryStep((s) => s + 1);
      }
      advanceTimerRef.current = null;
    }, 400);
  }, [setRating]);

  // Menu item functions
  const addMenuItem = useCallback((name: string, course: CourseType) => {
    const newItem: RatingMenuItem = {
      id: `new-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      name: name.trim(),
      course,
      rating: 0,
      pendingImages: [],
      isNew: true,
    };
    setMenuItems((prev) => [...prev, newItem]);
    haptics.selection();
  }, []);

  const updateMenuItem = useCallback((id: string, updates: Partial<RatingMenuItem>) => {
    setMenuItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  }, []);

  const removeMenuItem = useCallback((id: string) => {
    setMenuItems((prev) => prev.filter((item) => item.id !== id));
    haptics.selection();
  }, []);

  const addMenuItemImage = useCallback(
    (itemId: string, publicId: string, previewUrl: string) => {
      setMenuItems((prev) =>
        prev.map((item) => {
          if (item.id !== itemId) return item;
          if (item.pendingImages.length >= MAX_PENDING_IMAGES) return item;
          return {
            ...item,
            pendingImages: [...item.pendingImages, { publicId, previewUrl, status: 'pending' as const }],
          };
        })
      );
    },
    []
  );

  const removeMenuItemImage = useCallback((itemId: string, publicId: string) => {
    setMenuItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              pendingImages: item.pendingImages.filter((img) => img.publicId !== publicId),
            }
          : item
      )
    );
  }, []);

  /**
   * Updates a single pendingImages entry's status by (itemId, publicId).
   * Wired to `uploadPendingMenuItemImages`'s status callback during the
   * post-submit upload fan-out. Also surfaces a single non-blocking toast on
   * the first failure in a batch — the guest has already reached
   * CompletionStep by the time this can fire, so a blocking Alert would be
   * the wrong UI here.
   */
  const onImageStatusChange = useCallback(
    (itemId: string, publicId: string, status: 'uploading' | 'linked' | 'failed') => {
      setMenuItems((prev) =>
        prev.map((item) =>
          item.id !== itemId
            ? item
            : {
                ...item,
                pendingImages: item.pendingImages.map((img) =>
                  img.publicId === publicId ? { ...img, status } : img
                ),
              }
        )
      );
      if (status === 'failed' && !hasShownUploadFailureToastRef.current && !isClosedRef.current) {
        hasShownUploadFailureToastRef.current = true;
        toast({
          title: 'Some photos failed to upload',
          description: 'Your rating was saved, but some photos couldn’t be uploaded.',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [toast]
  );

  /** Hook-internal closure over `restaurantId`, bound for `uploadPendingMenuItemImages`. */
  const uploadImageFn = useCallback(
    (params: { menuItemId: number; publicId: string }) =>
      uploadMenuItemImageMutation({
        restaurantId,
        menuItemId: params.menuItemId,
        publicId: params.publicId,
        skipInvalidation: true,
      }),
    [uploadMenuItemImageMutation, restaurantId]
  );

  // Reset function — clears all state back to initial values.
  const reset = useCallback(() => {
    clearAdvanceTimer();
    clearNudgeTimer();
    setActiveView('ratings');
    setRatings({ ...EMPTY_RATINGS });
    setMenuItems([]);
    setCategoryStep(0);
    setNudge(false);
    advancedCategoriesRef.current.clear();
    hasShownUploadFailureToastRef.current = false;
    hasSubmittedRef.current = false;
    hasDecidedJumpRef.current = false;
    isClosedRef.current = true;
  }, []);

  // Submit function — does NOT call reset() on success.
  // The parent component (RestaurantRatingModal) is responsible for advancing
  // the view to 'completion' after a successful submit, which naturally
  // transitions the user away from the rating flow. Calling reset() here
  // would wipe displayed data before the completion screen has rendered.
  const submit = useCallback(async () => {
    hasSubmittedRef.current = true;
    try {
      // Build menu item ratings
      const menuItemRatings: SubmitMenuItemRating[] = menuItems
        .filter((item) => item.rating > 0)
        .map((item) => ({
          // Omit menuItemId entirely for new items rather than sending the
          // invalid placeholder 0 — the backend treats absence as "new item".
          ...(item.menuItemId !== undefined ? { menuItemId: item.menuItemId } : {}),
          name: item.name,
          selfRated: item.isNew,
          rating: item.rating,
          course: item.course,
          user: { firstName: '', lastName: '' }, // Will be filled by server
        }));

      const response = await submitRating({
        restaurantId,
        ratings: {
          restaurantRatings: ratings,
          // SubmitMenuItemRating has optional menuItemId for new items; server accepts and assigns the PK.
          // Cast required because MenuItemRating (response type) has required menuItemId.
          menuItemRatings: menuItemRatings as unknown as MenuItemRating[],
        },
      });

      // Haptic feedback for success (confetti fires on CompletionStep)
      haptics.celebrate();

      // Fire-and-forget — do NOT await. submit() must resolve as soon as the
      // ratings POST succeeds so CompletionStep shows immediately. A failed
      // image upload must never reject submit() itself or block reaching
      // CompletionStep for a rating that actually saved. Invalidation runs
      // once, after the whole batch settles, rather than per image — and is
      // skipped entirely when there was nothing to upload (uploadPendingMenuItemImages
      // early-returns for a photo-less submit, so there's nothing new to invalidate for).
      const hasPendingImages = menuItems.some((item) =>
        item.pendingImages.some((img) => img.status === 'pending')
      );
      uploadPendingMenuItemImages(
        menuItems,
        response.menuItemRatings ?? null,
        uploadImageFn,
        onImageStatusChange
      )
        .then(() => {
          if (!hasPendingImages) return;
          queryClient.invalidateQueries({ queryKey: restaurantKeys.menuItems(restaurantId) });
          queryClient.invalidateQueries({ queryKey: restaurantKeys.detail(restaurantId) });
        })
        .catch((e) => {
          // Report only — never surface as a blocking Alert on a screen the
          // guest has already moved past. Per-image failures are already
          // reported via onImageStatusChange's toast; this only catches a
          // failure in the fan-out setup itself.
          console.error('Failed to upload pending menu item photos:', e);
        });
    } catch (error) {
      console.error('Failed to submit rating:', error);
      haptics.error();
      throw error;
    }
  }, [restaurantId, ratings, menuItems, submitRating, uploadImageFn, onImageStatusChange, queryClient]);

  return {
    // View navigation
    activeView,
    goToRatings,
    goToCompletion,

    // Restaurant ratings
    ratings,

    // Category-step navigation
    categoryStep,
    currentCategory,
    isLastCategory,
    canGoNextCategory,
    canGoPrevCategory,
    swipeDirection,
    nudge,
    nudgeAnnounceKey,
    globalStep,
    goNextCategoryStep,
    goPrevCategoryStep,
    goToStep,
    setRatingAndAdvance,

    // Menu items
    menuItems,
    addMenuItem,
    updateMenuItem,
    removeMenuItem,
    addMenuItemImage,
    removeMenuItemImage,

    // Submission
    submit,
    isSubmitting,
    isLoading,
    reset,

    // Load error state
    loadError,
    onRetry: refetch,
  };
}

export default useRestaurantRating;
